// Répétition par personne.
//
// Certaines listes décrivent des personnes (les personnes à charge de l'IMM
// 0008 : l'époux, chaque enfant) et chaque personne a une quarantaine de
// renseignements à fournir. Tout entasser dans une fiche de liste donnait un
// écran de 30 cases en texte libre, sans condition possible : un enfant de 3 ans
// voyait « nom de l'époux ».
//
// Une liste dont une colonne porte `roster` se remplit donc en deux temps :
//   1. la liste elle-même ne montre que les colonnes `roster` (qui est-ce) ;
//   2. chaque autre colonne devient une vraie question, posée une fois par
//      personne, avec son prénom dans le texte et ses propres conditions.
//
// Rien ne change dans la façon d'enregistrer : la réponse reste
// `answers[groupe] = [{ _id, prénom, …, colonne: valeur }, …]`. La console, la
// traduction des réponses et le remplissage du PDF lisent donc toujours la même
// liste. Seul le questionnaire fabrique, à la volée, un écran par colonne et par
// personne, identifié par `groupe#idLigne#colonne`.
//
// L'identifiant de ligne (`_id`) est ce qui rend la reprise sûre : la position
// du client est enregistrée par identifiant de question, et un index (« la
// 2e personne ») changerait de sens dès qu'il retire quelqu'un de la liste.

import type { MultiEntryFieldDef, Question } from "@/data/questions";

export const REPEAT_SEP = "#";

export type Row = Record<string, any>;

export const isRepeatGroup = (q: Pick<Question, "type" | "multiEntryFields"> | undefined | null): boolean =>
  !!q && q.type === "multi-entry" && !!q.multiEntryFields?.some((f) => f.roster);

export const rosterFields = (q: Pick<Question, "multiEntryFields">): MultiEntryFieldDef[] =>
  (q.multiEntryFields ?? []).filter((f) => f.roster);

export const virtualId = (groupId: string, rowId: string, key: string) =>
  [groupId, rowId, key].join(REPEAT_SEP);

/** `groupe#ligne#colonne` → ses trois parties ; null pour une question ordinaire. */
export function parseVirtualId(id: string): { groupId: string; rowId: string; key: string } | null {
  const first = id.indexOf(REPEAT_SEP);
  if (first <= 0) return null;
  const second = id.indexOf(REPEAT_SEP, first + 1);
  if (second <= first + 1) return null;
  return { groupId: id.slice(0, first), rowId: id.slice(first + 1, second), key: id.slice(second + 1) };
}

export const newRowId = () =>
  `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/**
 * Donne un `_id` aux lignes qui n'en ont pas (listes remplies avant la
 * répétition). L'identifiant dépend de la position, donc il est stable tant que
 * la liste n'est pas réenregistrée ; le premier enregistrement le fige.
 */
export function ensureRowIds<T extends Record<string, any>>(data: T, groupIds: Iterable<string>): T {
  let out = data;
  for (const g of groupIds) {
    const rows = data?.[g];
    if (!Array.isArray(rows) || rows.every((r) => r && typeof r === "object" && r._id)) continue;
    out = {
      ...out,
      [g]: rows.map((r, i) => (r && typeof r === "object" && !r._id ? { ...r, _id: `l${i}` } : r)),
    };
  }
  return out;
}

/** Lit une réponse, qu'elle soit ordinaire ou rangée dans la ligne d'une personne. */
export function readAnswer(data: Record<string, any> | undefined, id: string): any {
  if (!data) return undefined;
  const v = parseVirtualId(id);
  if (!v) return data[id];
  // Un document d'une personne (le fichier de son passeport) est rangé à plat,
  // sous l'identifiant de son écran : il passe par le téléversement comme tout
  // document, et un fichier n'a rien à faire dans une ligne de liste.
  if (Object.prototype.hasOwnProperty.call(data, id)) return data[id];
  const rows = data[v.groupId];
  if (!Array.isArray(rows)) return undefined;
  return rows.find((r) => r?._id === v.rowId)?.[v.key];
}

/** Une date se range dans une ligne en AAAA-MM-JJ : la base refuse un objet
 *  Date au milieu d'une liste, et c'est la forme que lisent l'âge et le PDF. */
const toRowValue = (value: any) =>
  value instanceof Date && !isNaN(value.getTime())
    ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
    : value;

/** Écrit une réponse ; pour une personne, remplace sa seule case dans la liste. */
export function writeAnswer<T extends Record<string, any>>(data: T, id: string, raw: any): T {
  const v = parseVirtualId(id);
  if (!v) return { ...data, [id]: raw };
  const value = toRowValue(raw);
  const rows = Array.isArray(data[v.groupId]) ? (data[v.groupId] as Row[]) : [];
  return {
    ...data,
    [v.groupId]: rows.map((r) => (r?._id === v.rowId ? { ...r, [v.key]: value } : r)),
  };
}

/** Âge en années révolues à la date du jour, ou null si la date est illisible. */
export function ageFrom(dateOfBirth: unknown, today = new Date()): number | null {
  const m = String(toRowValue(dateOfBirth) ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = +m[3];
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < mo || (today.getMonth() + 1 === mo && today.getDate() < d)) age -= 1;
  return age;
}

/**
 * Les colonnes dont la valeur nomme chaque ligne, dans l'ordre de préférence.
 * Par défaut le prénom puis le nom, parce que la première liste à répétition
 * décrivait des personnes ; une liste qui décrit autre chose (une occupation, un
 * diplôme, un séjour) marque `nameKey` sur la colonne qui la désigne.
 */
export const nameKeys = (fields: MultiEntryFieldDef[]): string[] => {
  const marques = fields.filter((f) => f.roster && f.nameKey).map((f) => f.key);
  return marques.length ? marques : ["firstName", "lastName"];
};

/** Vrai quand les lignes décrivent des personnes : elles ont un prénom. */
export const isPersonGroup = (
  q: Pick<Question, "multiEntryFields"> | undefined | null,
): boolean => !!q?.multiEntryFields?.some((f) => f.roster && f.key === "firstName");

export function personName(
  row: Row,
  index: number,
  fallback: (n: number) => string,
  keys: string[] = ["firstName", "lastName"],
): string {
  for (const k of keys) {
    const v = String(row?.[k] ?? "").trim();
    if (v) return v;
  }
  return fallback(index + 1);
}

const fill = (text: string | undefined, name: string) =>
  text ? text.split("{name}").join(name) : text;

/** Les colonnes qui s'appliquent à une personne (roster toujours ; âge et
 *  conditions tranchés en chaîne sur sa ligne). */
export function activeColumnKeys(fields: MultiEntryFieldDef[], row: Row): Set<string> {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const age = ageFrom(row?.dateOfBirth);
  const memo = new Map<string, boolean>();
  const active = (key: string, seen: Set<string> = new Set()): boolean => {
    const f = byKey.get(key);
    if (!f) return true; // une colonne inconnue ne bloque rien
    if (f.roster) return true;
    if (memo.has(key)) return memo.get(key)!;
    if (seen.has(key)) return false; // boucle de conditions : on écarte
    seen.add(key);
    let ok = true;
    if (age !== null && f.minAge !== undefined && age < f.minAge) ok = false;
    if (age !== null && f.maxAge !== undefined && age > f.maxAge) ok = false;
    if (ok && f.dependsOn) {
      const conds = Array.isArray(f.dependsOn) ? f.dependsOn : [f.dependsOn];
      ok = conds.some((c) => {
        if (!active(c.key, seen)) return false;
        const actual = row?.[c.key];
        const expected = Array.isArray(c.value) ? c.value : [c.value];
        if (Array.isArray(actual)) return actual.some((a) => expected.includes(a));
        return expected.includes(actual);
      });
    }
    memo.set(key, ok);
    return ok;
  };
  return new Set(fields.filter((f) => active(f.key)).map((f) => f.key));
}

/**
 * La liste telle qu'on l'enregistre : chaque ligne sans les cases de colonnes
 * qui ne s'appliquent plus à sa personne (et sans leur raison de « passer »).
 * Sinon un numéro de passeport tapé avant que le client réponde « non » à
 * « passeport valide » partirait quand même dans le PDF.
 */
export function pruneInactive(group: Question, rows: any): any {
  if (!Array.isArray(rows)) return rows;
  const fields = group.multiEntryFields ?? [];
  const known = new Set(fields.map((f) => f.key));
  return rows.map((row) => {
    if (!row || typeof row !== "object") return row;
    const keep = activeColumnKeys(fields, row);
    const out: Row = {};
    for (const [k, val] of Object.entries(row)) {
      const base = k.endsWith("__skipReason") ? k.slice(0, -"__skipReason".length) : k.replace(/_autrePrecision$/, "");
      if (known.has(base) && !keep.has(base)) continue;
      out[k] = val;
    }
    return out;
  });
}

/** Le nom qui désigne chaque personne : son prénom, ou prénom et nom quand
 *  deux personnes portent le même prénom, ou un numéro en dernier recours. */
export function displayNames(
  rows: Row[],
  fallback: (n: number) => string,
  fields: MultiEntryFieldDef[] = [],
): string[] {
  const keys = nameKeys(fields);
  const first = rows.map((r, i) => personName(r, i, fallback, keys));
  const full = rows.map((r, i) => {
    const f = keys.map((k) => String(r?.[k] ?? "").trim()).filter(Boolean).join(" ");
    return f || first[i];
  });
  const count = (arr: string[], v: string) => arr.filter((x) => x === v).length;
  return rows.map((_, i) => {
    if (count(first, first[i]) === 1) return first[i];
    if (count(full, full[i]) === 1) return full[i];
    return `${full[i]} (${i + 1})`;
  });
}

/**
 * Les écrans d'une personne : une question par colonne non `roster` qui
 * s'applique à elle (activeColumnKeys). La liste est recalculée à chaque rendu
 * avec les réponses du moment, donc un écran apparaît ou disparaît dès que la
 * réponse qui le commande change.
 */
export function expandGroup(
  group: Question,
  data: Record<string, any>,
  fallbackName: (n: number) => string,
): Question[] {
  const rows = Array.isArray(data?.[group.id]) ? (data[group.id] as Row[]) : [];
  const all = group.multiEntryFields ?? [];
  const names = displayNames(rows, fallbackName, rosterFields(group));
  const out: Question[] = [];
  rows.forEach((row, rowIndex) => {
    if (!row?._id) return;
    const name = names[rowIndex];
    const keep = activeColumnKeys(all, row);
    for (const f of all) {
      if (f.roster || !keep.has(f.key)) continue;
      out.push({
        id: virtualId(group.id, row._id, f.key),
        type: f.type === "boolean" ? "yes-no" : (f.type ?? "text"),
        label: fill(f.question, name) || `${name} : ${f.label}`,
        indication: fill(f.indication, name) ?? "",
        help: fill(f.help, name) ?? "",
        example: fill(f.example, name),
        placeholder: f.placeholder,
        whyImportant: f.whyImportantReason
          ? { reason: fill(f.whyImportantReason, name)!, consequence: fill(f.whyImportantConsequence, name) }
          : undefined,
        isRequired: f.required ?? group.isRequired,
        successMessage: group.successMessage,
        options: f.options,
        // Chaque personne forme sa propre section : le client voit à qui se
        // rapportent les questions. La clé de section reste celle de la liste,
        // pour qu'une section masquée par le cabinet masque aussi ses écrans.
        section: name,
        sectionKey: group.sectionKey || group.section,
        repeatOf: {
          groupId: group.id,
          rowId: row._id,
          key: f.key,
          rowIndex,
          personName: [row.firstName, row.lastName].filter(Boolean).join(" ").trim() || undefined,
          ocr: f.ocr,
        },
      });
    }
  });
  return out;
}

/** Un écran de personne dont la réponse est un fichier : rangé à plat. */
export const isFlatVirtual = (q: Pick<Question, "type" | "repeatOf"> | undefined | null): boolean =>
  !!q?.repeatOf && q.type === "document";

/**
 * La configuration de lecture d'un document de personne : celle du document
 * d'origine (le passeport du client), dont chaque case vise la colonne de CETTE
 * personne. Une case sans colonne correspondante dans la liste est retirée, et
 * une valeur lue passe par la table `values` de la colonne.
 */
export function personOcrConfig<C extends { fieldMapping: Record<string, { wizardFieldId: string; transform?: (raw: any) => any }> }>(
  q: Question,
  base: C | undefined,
  group: Question | undefined,
): C | undefined {
  const ocr = q.repeatOf?.ocr;
  if (!q.repeatOf || !ocr || !base) return undefined;
  // Les colonnes de la liste (prénom, nom, date de naissance…) ont été tapées
  // par le client exprès : la lecture ne les écrase pas, elle sert à vérifier.
  const columns = new Set((group?.multiEntryFields ?? []).filter((f) => !f.roster).map((f) => f.key));
  const fieldMapping: C["fieldMapping"] = {} as C["fieldMapping"];
  for (const [source, m] of Object.entries(base.fieldMapping)) {
    const column = ocr.fields?.[m.wizardFieldId] ?? m.wizardFieldId;
    if (!columns.has(column)) continue;
    const table = ocr.values?.[column];
    const transform = table
      ? (raw: any) => {
          const v = m.transform ? m.transform(raw) : raw;
          return v != null && table[String(v)] !== undefined ? table[String(v)] : v;
        }
      : m.transform;
    (fieldMapping as any)[source] = {
      ...m,
      wizardFieldId: virtualId(q.repeatOf.groupId, q.repeatOf.rowId, column),
      transform,
    };
  }
  return { ...base, fieldMapping };
}

/** Pose plusieurs réponses d'un coup, chacune à sa place (ligne ou à plat). */
export function mergeAnswers<T extends Record<string, any>>(data: T, values: Record<string, any>): T {
  let out = data;
  for (const [id, value] of Object.entries(values)) out = writeAnswer(out, id, value);
  return out;
}

/** « Awa », « Awa et Ibrahima », « Awa, Ibrahima et Fatou ». */
export function joinNames(names: string[], language = "fr"): string {
  const and = language === "en" ? " and " : language === "es" ? " y " : " et ";
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")}${and}${names[names.length - 1]}`;
}

/** Le libellé d'une question qui nomme les personnes déjà entrées dans une
 *  liste ; le libellé normal quand aucune ne correspond. */
export function namedLabelFor(q: Question, data: Record<string, any>, language = "fr"): string {
  const n = q.namedLabel;
  if (!n?.text) return q.label;
  const rows = Array.isArray(data?.[n.list]) ? (data[n.list] as Row[]) : [];
  const expected = n.where ? (Array.isArray(n.where.value) ? n.where.value : [n.where.value]) : null;
  const names = rows
    .filter((r) => r && (!expected || expected.includes(r[n.where!.key])))
    .map((r) => String(r.firstName ?? "").trim() || String(r.lastName ?? "").trim())
    .filter(Boolean);
  if (names.length === 0) return n.textNone || q.label;
  return n.text.split("{names}").join(joinNames(names, language));
}
